import { computed, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { auditTime, Subject } from 'rxjs';
import { StateMemento } from 'wflow-cdk';
import { NavigationMode } from 'wflow-features/navigation';
import {
  WfNavigationStore,
  WfNavigationStoreState,
} from 'wflow-features/navigation/interfaces/nav-store-state.interface';
import { PageContainerService } from '../../components/page-container/page-container.service';

/**
 * Interface defining the structure of navigation state cache
 */
interface NavStateCache {
  size: number;
  openSize: number;
  mode: NavigationMode;
  globalOpen: boolean;
  detailOpen: boolean;

  /**
   * [nodeId, timestamp]
   * timestamp is used to determine if the node is open or closed (if the timestamp is older than 1 week, it is considered closed)
   */
  openNodes: [string, number][];
}

/**
 * Default navigation state values
 */
const defaultState: NavStateCache = {
  size: 300,
  openSize: 300,
  mode: 'auto',
  detailOpen: false,
  globalOpen: true,
  openNodes: [],
};

/**
 * Time to live for open nodes
 * 1 week
 */
const OPEN_NODE_TTL = 1000 * 60 * 60 * 24 * 7;

const NAV_STATE_CACHE_DEBOUNCE_TIME = 1000;

/**
 * Service managing navigation state persistence and caching
 */
@Injectable({
  providedIn: 'root',
})
export class NavStateCacheService implements WfNavigationStore {
  private readonly context = inject(PageContainerService).navContext;
  private readonly store = new StateMemento<NavStateCache>(
    {
      key: 'NAVIGATION_STATE',
      storage: localStorage,
    },
    defaultState,
  );

  // Memoized state and context values
  private readonly _state = signal<NavStateCache>(this.store.state);
  private readonly _isDetailContext = computed(
    () => this.context() === 'detail',
  );
  private readonly _activeOpenProperty = computed(() =>
    this._isDetailContext() ? 'detailOpen' : 'globalOpen',
  );

  /**
   * Computed navigation state based on current context
   */
  readonly state = computed<WfNavigationStoreState>(() => {
    const state = this._state();
    const isOpen = state[this._activeOpenProperty()];
    const size = isOpen ? state.size || state.openSize : 0;
    const openNodes = this.cleanExpiredOpenNodes(state.openNodes);

    return {
      size,
      openSize: state.openSize,
      mode: state.mode,
      isOpen,
      openNodes: openNodes.map(([nodeId]) => nodeId),
    };
  });

  private readonly _stateQueue$ = new Subject<NavStateCache>();

  constructor() {
    this._stateQueue$
      .pipe(auditTime(NAV_STATE_CACHE_DEBOUNCE_TIME), takeUntilDestroyed())
      .subscribe(() => {
        this.store.change(this._state());
      });
  }

  onOpenNodeChange(nodeId: string, isOpen: boolean) {
    const state = { ...(this._state() ?? defaultState) };
    let openNodes = this.cleanExpiredOpenNodes(state.openNodes);

    try {
      if (isOpen) {
        const expired = Date.now() + OPEN_NODE_TTL;
        const node = openNodes.find(([id]) => id === nodeId);
        if (node) {
          node[1] = expired;
        } else {
          openNodes.push([nodeId, expired]);
        }

        state.openNodes = openNodes;
      } else {
        state.openNodes = openNodes.filter(([n]) => n !== nodeId);
      }

      this._state.set(state);
      this._stateQueue$.next(state);
    } catch (error) {
      console.error('Error updating navigation state', error);
    }
  }

  /**
   * Updates navigation state and persists changes
   */
  setState(state: Partial<WfNavigationStoreState>) {
    if (!state) return;

    const currentState = this._state();
    const value: NavStateCache = { ...defaultState, ...currentState };
    const openProperty = this._activeOpenProperty();

    // Optimize state updates by checking for actual changes
    let hasChanges = false;

    for (const key in state) {
      if (key === 'isOpen') {
        if (value[openProperty] !== state.isOpen) {
          value[openProperty] = state.isOpen;
          hasChanges = true;
        }
        continue;
      }
      if (value[key] !== state[key]) {
        value[key] = state[key];
        hasChanges = true;
      }
    }

    // Always update the state
    this._state.set(value);

    if (hasChanges) {
      // Only update the store if there are actual changes
      this._stateQueue$.next(value);
    }
  }

  private cleanExpiredOpenNodes(openNodes: [string, number][]) {
    try {
      const now = Date.now();
      const newOpenNodes = [];

      for (const [nodeId, expired] of openNodes) {
        if (expired - now > 0) {
          newOpenNodes.push([nodeId, expired]);
        }
      }

      return newOpenNodes;
    } catch (error) {
      console.error('Error cleaning expired open nodes', error);
      return [];
    }
  }
}
